import IconStar from '@/assets/icons/product/icon-star.svg';
import CustomModal from '@/components/ui/customModal';
import Input from '@/components/ui/input';
import { useReviewCreateMutation } from '@/graphql';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { RiCloseLargeLine } from 'react-icons/ri';
import { Button, Rate } from 'rsuite';
import styles from './styles.module.scss';

type PropsModal = {
  open: boolean;
  close: (e: boolean) => void;
  productId?: string;
  data: any;
};

export default function UserReviewModal({
  open,
  close,
  productId,
  data,
}: PropsModal) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    worth: string;
    flaws: string;
    comment: string;
    stars: number;
  }>({
    defaultValues: {
      worth: '',
      flaws: '',
      comment: '',
      stars: 1,
    },
  });

  const [submitted, setSubmitted] = useState(false);
  const [reviewCreate, { loading, error }] = useReviewCreateMutation();
  const t = useTranslations('Drawer.user-review-modal');
  const route = useRouter();

  const onSubmit = async (data: {
    worth: string;
    flaws: string;
    comment: string;
    stars: number;
  }) => {
    if (!productId) {
      console.error('Product ID is required to submit a review.');
      return;
    }

    try {
      await reviewCreate({
        variables: {
          input: {
            productVariant: productId,
            rating: data.stars,
            advantages: data.worth,
            disadvantages: data.flaws,
            description: data.comment,
          },
        },
        context: {
          requiresAuth: true,
        },
      });

      setSubmitted(true);
    } catch (err) {
      console.error('Failed to create review:', err);
    }
  };

  const handleCloseFeedbackMessage = () => {
    close(false);
    route.push('/');
  };

  return (
    <CustomModal
      closeOnOutsideClick
      open={open}
      close={close}
      className={`${styles.modal} ${submitted ? styles.modalSubmitted : ''}`}
    >
      <div className={styles.modalContainer}>
        <div>
          <div
            className={styles.modalTitle}
            style={{ justifyContent: submitted ? 'flex-end' : 'space-between' }}
          >
            {!submitted ? t('send-feedback') : ''}
            <RiCloseLargeLine
              className={styles.modalClose}
              onClick={() => close(false)}
            />
          </div>
        </div>
        {submitted ? (
          <div className={styles.wrapperFeedBackMessage}>
            <p className={styles.thankYouMessage}>
              {t('feedback-appear-soon')}
            </p>
            <Button
              className={styles.feedBackButton}
              onClick={handleCloseFeedbackMessage}
            >
              Продолжить покупки
            </Button>
          </div>
        ) : (
          <div className={styles.modalBody}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className={styles.modalForm}
            >
              <div className={styles.modalRateContainer}>
                <div className={styles.modalRateTitle}>{t('how-product')}</div>
                <Controller
                  name="stars"
                  control={control}
                  rules={{ required: 'Оценка обязательна' }}
                  render={({ field }) => (
                    <Rate
                      {...field}
                      defaultValue={0}
                      character={<IconStar />}
                      size="xs"
                      color="yellow"
                      className={styles.rateContainer}
                    />
                  )}
                />
              </div>
              <div className={styles.modalInfoContainer}>
                <div className={styles.modalInfoTitle}>
                  {t('summary-helper-text')}
                </div>
              </div>
              <Controller
                name="worth"
                control={control}
                rules={{ required: t('abligatory-input.required') }}
                render={({ field }) => (
                  <Input
                    state={field.value}
                    setState={field.onChange}
                    label={t('abligatory-input.label')}
                    error={errors}
                    name="worth"
                    className={styles.inputnorm}
                    style={{ height: '100%' }}
                  />
                )}
              />

              <Controller
                name="flaws"
                control={control}
                rules={{ required: t('shortcoming-input.required') }}
                render={({ field }) => (
                  <Input
                    state={field.value}
                    setState={field.onChange}
                    label={t('shortcoming-input.label')}
                    error={errors}
                    name="flaws"
                    className={styles.inputnorm}
                    style={{ height: '100%' }}
                  />
                )}
              />

              <Controller
                name="comment"
                control={control}
                rules={{ required: t('comments-input.required') }}
                render={({ field }) => (
                  <Input
                    name="comment"
                    error={errors}
                    state={field.value}
                    setState={field.onChange}
                    label={t('comments-input.label')}
                    className={styles.inputnorm}
                    style={{ height: '100%' }}
                  />
                )}
              />

              <div className={styles.modalFooter}>
                <Button type="submit" className={styles.save} loading={loading}>
                  {t('send-feedback')}
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </CustomModal>
  );
}
